/**
 * MarketingNav — the single, shared top navigation for every public /
 * marketing surface (Welcome, Journey, Use cases, Docs, How-to).
 *
 * Previously each page rendered its own header: Welcome had a rich sticky nav
 * while Journey / Use cases / Docs only got a bare wordmark + theme toggle from
 * Layout. That inconsistency is the "different menus" problem. This component
 * is now mounted once in Layout's public branch so the menu is identical
 * everywhere.
 *
 * Design language mirrors LangChain / Linear marketing headers: a sticky,
 * blurred bar, a wordmark, a compact set of route links with an animated accent
 * underline on hover, and right-aligned auth CTAs. No emojis — lucide icons
 * only. Self-contained Tailwind (theme tokens: bg / border / text / muted /
 * accent) so it works outside the Welcome `.welcome-fx` scope.
 */
import { Link, useRouterState } from "@tanstack/react-router";
import { Github, Sun, Moon, ArrowRight } from "lucide-react";
import { useApp } from "@/lib/store";
import { BrandLogo, BrandMark } from "@/components/BrandLogo";

const LINKS: { to: string; label: string }[] = [
  { to: "/#prototypes", label: "Prototypes" },
  { to: "/journey", label: "Journey" },
  { to: "/use-cases", label: "Use cases" },
  { to: "/docs", label: "Docs" },
];

export function MarketingNav() {
  const token = useApp((s) => s.token);
  const logout = useApp((s) => s.logout);
  const theme = useApp((s) => s.theme);
  const toggleTheme = useApp((s) => s.toggleTheme);
  const path = useRouterState({ select: (s) => s.location.pathname });

  return (
    <nav data-marketing-nav className="sticky top-0 z-50 border-b border-border bg-bg/90 backdrop-blur-md">
      <div className="mx-auto flex min-h-[76px] w-full max-w-[1440px] flex-wrap items-center justify-between gap-4 px-6 py-3">
        <Link to="/" className="flex shrink-0 items-center gap-2.5 font-bold tracking-tight">
          <BrandMark className="h-8 w-8 sm:hidden" priority alt="Ontoligent" />
          <BrandLogo className="hidden h-8 w-auto sm:block" priority alt="Ontoligent" />
        </Link>

        <div className="order-3 flex min-w-0 flex-1 basis-full items-center gap-1 overflow-x-auto scroll-thin sm:order-none sm:basis-auto sm:justify-center">
          {LINKS.map((l) => {
            const activeLink = path === l.to;
            const className = [
              "group relative whitespace-nowrap px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.08em] transition-colors",
              activeLink ? "text-text" : "text-muted hover:text-text",
            ].join(" ");
            const label = <>
              {l.label}
              <span
                className={[
                  "pointer-events-none absolute inset-x-3 -bottom-0.5 h-px origin-left bg-accent transition-transform duration-200",
                  activeLink ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100",
                ].join(" ")}
              />
            </>;
            return l.to === "/#prototypes" ? (
              <a key={l.to} href={l.to} className={className}>{label}</a>
            ) : (
              <Link key={l.to} to={l.to} className={className}>{label}</Link>
            );
          })}
          <a
            href="https://github.com/redpillsoftware7/ontolligence"
            target="_blank"
            rel="noopener"
            className="group relative flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.08em] text-muted transition-colors hover:text-text"
          >
            <Github size={14} /> GitHub
          </a>
        </div>

        <div className="order-2 flex items-center gap-2 sm:order-none">
          <button
            onClick={toggleTheme}
            className="btn-ghost"
            aria-label="Toggle theme"
            title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
          >
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          {token ? (
            <>
              <Link to="/dashboard" className="btn-primary inline-flex items-center gap-1">
                Open Studio <ArrowRight size={14} />
              </Link>
              <button type="button" onClick={logout} className="btn-ghost hidden text-sm sm:inline-flex">
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-ghost hidden text-sm sm:inline-flex">
                Sign in
              </Link>
              <Link to="/signup" className="btn-primary inline-flex items-center gap-1">
                Get started <ArrowRight size={14} />
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
